<template>
  <v-layout>
    <!-- Single WIDGET_PRICE -->
    <overlay-data-widget-price
      v-if="widget.kind === 'WIDGET_PRICE'"
      ref="actualWidget"
      style="max-width: 380px"
      :name="widget.name"
      :old-price="widget.field_mapping.old_price"
      :price-prefix="widget.field_mapping.price_prefix"
      :price-subline="widget.field_mapping.price_subline"
      :price="widget.field_mapping.price"
      :rebate-enabled="widget.field_mapping.rebate_enabled"
      :rebate-subline="widget.field_mapping.rebate_subline"
      :warnings="getExtraWarningsForWidget(widget)"
      class="mb-3"
      @changed="$emit('changed')"
    />
    <!-- Single WIDGET_TEXT -->
    <overlay-data-widget-text
      v-if="widget.kind === 'WIDGET_TEXT'"
      ref="actualWidget"
      style="max-width: 380px"
      :name="widget.name"
      :text1="widget.field_mapping.text1"
      :text2="widget.field_mapping.text2"
      :text3="widget.field_mapping.text3"
      :text-alignment="widget.field_mapping.text_alignment"
      :text-color="widget.field_mapping.text_color"
      :text-size="widget.field_mapping.text_size"
      :background-color="widget.field_mapping.background_color"
      :color-palette="colorPalette"
      :warnings="getExtraWarningsForWidget(widget)"
      class="mb-3"
      @changed="$emit('changed')"
    />
    <!-- Single WIDGET_IMAGE -->
    <overlay-data-widget-image
      v-if="widget.kind === 'WIDGET_IMAGE'"
      ref="actualWidget"
      style="max-width: 380px"
      :name="widget.name"
      :image="widget.field_mapping.image"
      :size="widget.field_mapping.size"
      :color="widget.field_mapping.color"
      :color-palette="colorPalette"
      :warnings="getExtraWarningsForWidget(widget)"
      class="mb-3"
      @changed="$emit('changed')"
      @change-image="$emit('change-image', $event)"
    />
    <!-- Single WIDGET_BUTTON -->
    <overlay-data-widget-button
      v-if="widget.kind === 'WIDGET_BUTTON'"
      ref="actualWidget"
      style="max-width: 380px"
      :name="widget.name"
      :text="widget.field_mapping.text"
      :size="widget.field_mapping.size"
      :position-x="widget.field_mapping.position_x"
      :position-y="widget.field_mapping.position_y"
      :text-color="widget.field_mapping.text_color"
      :background-color="widget.field_mapping.background_color"
      :color-palette="colorPalette"
      :warnings="getExtraWarningsForWidget(widget)"
      class="mb-3"
      @changed="$emit('changed')"
    />
    <!-- Single WIDGET_FRAME -->
    <overlay-data-widget-frame
      v-if="widget.kind === 'WIDGET_FRAME'"
      ref="actualWidget"
      style="max-width: 380px"
      :name="widget.name"
      :thickness="widget.field_mapping.thickness"
      :color="widget.field_mapping.color"
      :color-palette="colorPalette"
      :warnings="getExtraWarningsForWidget(widget)"
      class="mb-3"
      @changed="$emit('changed')"
    />
    <!-- WIDGET_LAYOUT_COLUMNS

    This widget has a .columns list, with a list of field/widget names
    each. The resulting layout is similar to a masonry layout (avoiding
    empty vertical space between rows/fields with different heights),
    when the fields are assigned somewhat equally among columns.

    We are not using a library like vue-masonry-css here as it gives too
    little control over how fields are distributed among columns.

    -->
    <v-layout
      v-if="widget.kind === 'WIDGET_LAYOUT_COLUMNS'"
      :column="$vuetify.breakpoint.xs"
    >
      <v-layout
        v-for="(col, colIndex) in widget.columns"
        :key="'renderable-' + renderableIndex + '-layout-col-' + colIndex"
        class="grow-1"
        column
      >
        <template v-for="(colFieldOrWidget, colFieldOrWidgetIndex) in col">
          <div
            v-if="colFieldOrWidget === null"
            :key="
              'renderable-' +
                renderableIndex +
                '-layout-col-' +
                colIndex +
                '-field-' +
                colFieldOrWidgetIndex +
                '-null-placeholder'
            "
            class="grow-1"
          />
          <!-- Any field within WIDGET_LAYOUT_COLUMNS -->
          <overlay-data-field
            v-else-if="isField(colFieldOrWidget.name)"
            :key="
              'renderable-' +
                renderableIndex +
                '-layout-column-field-' +
                colFieldOrWidgetIndex
            "
            ref="wrappedFields"
            :field="colFieldOrWidget"
            :warning="getExtraWarningForField(colFieldOrWidget)"
            :color-palette="colorPalette"
            class="overlay-data-field px-2"
            :style="
              colFieldOrWidget.data.css ? colFieldOrWidget.data.css.form : null
            "
            @changed="$emit('changed')"
            @change-image="$emit('change-image', $event)"
          />
          <!-- WIDGET_PRICE within WIDGET_LAYOUT_COLUMNS -->
          <overlay-data-widget-price
            v-else-if="
              isWidget(colFieldOrWidget.name) &&
                colFieldOrWidget.kind === 'WIDGET_PRICE'
            "
            ref="wrappedWidgets"
            :key="
              'renderable-' +
                renderableIndex +
                '-layout-column-widget-price-' +
                colFieldOrWidgetIndex
            "
            :name="colFieldOrWidget.name"
            :old-price="colFieldOrWidget.field_mapping.old_price"
            :price-prefix="colFieldOrWidget.field_mapping.price_prefix"
            :price-subline="colFieldOrWidget.field_mapping.price_subline"
            :price="colFieldOrWidget.field_mapping.price"
            :rebate-enabled="colFieldOrWidget.field_mapping.rebate_enabled"
            :rebate-subline="colFieldOrWidget.field_mapping.rebate_subline"
            :warnings="getExtraWarningsForWidget(colFieldOrWidget)"
            class="px-2 mb-3"
            @changed="$emit('changed')"
          />
          <!-- WIDGET_TEXT within WIDGET_LAYOUT_COLUMNS -->
          <overlay-data-widget-text
            v-else-if="
              isWidget(colFieldOrWidget.name) &&
                colFieldOrWidget.kind === 'WIDGET_TEXT'
            "
            ref="wrappedWidgets"
            :key="
              'renderable-' +
                renderableIndex +
                '-layout-column-widget-text-' +
                colFieldOrWidgetIndex
            "
            class="mb-3"
            :name="colFieldOrWidget.name"
            :text1="colFieldOrWidget.field_mapping.text1"
            :text2="colFieldOrWidget.field_mapping.text2"
            :text3="colFieldOrWidget.field_mapping.text3"
            :text-alignment="colFieldOrWidget.field_mapping.text_alignment"
            :text-color="colFieldOrWidget.field_mapping.text_color"
            :text-size="colFieldOrWidget.field_mapping.text_size"
            :background-color="colFieldOrWidget.field_mapping.background_color"
            :color-palette="colorPalette"
            :warnings="getExtraWarningsForWidget(colFieldOrWidget)"
            @changed="$emit('changed')"
          />
          <!-- WIDGET_IMAGE within WIDGET_LAYOUT_COLUMNS -->
          <overlay-data-widget-image
            v-else-if="
              isWidget(colFieldOrWidget.name) &&
                colFieldOrWidget.kind === 'WIDGET_IMAGE'
            "
            ref="wrappedWidgets"
            :key="
              'renderable-' +
                renderableIndex +
                '-layout-column-widget-image-' +
                colFieldOrWidgetIndex
            "
            :name="colFieldOrWidget.name"
            :image="colFieldOrWidget.field_mapping.image"
            :size="colFieldOrWidget.field_mapping.size"
            :color="colFieldOrWidget.field_mapping.color"
            :color-palette="colorPalette"
            :warnings="getExtraWarningsForWidget(colFieldOrWidget)"
            class="mb-3"
            @changed="$emit('changed')"
            @change-image="$emit('change-image', $event)"
          />
          <!-- WIDGET_BUTTON within WIDGET_LAYOUT_COLUMNS -->
          <overlay-data-widget-button
            v-else-if="
              isWidget(colFieldOrWidget.name) &&
                colFieldOrWidget.kind === 'WIDGET_BUTTON'
            "
            ref="wrappedWidgets"
            :key="
              'renderable-' +
                renderableIndex +
                '-layout-column-widget-button-' +
                colFieldOrWidgetIndex
            "
            class="mb-3"
            :name="colFieldOrWidget.name"
            :text="colFieldOrWidget.field_mapping.text"
            :size="colFieldOrWidget.field_mapping.size"
            :position-x="colFieldOrWidget.field_mapping.position_x"
            :position-y="colFieldOrWidget.field_mapping.position_y"
            :text-color="colFieldOrWidget.field_mapping.text_color"
            :background-color="colFieldOrWidget.field_mapping.background_color"
            :color-palette="colorPalette"
            :warnings="getExtraWarningsForWidget(colFieldOrWidget)"
            @changed="$emit('changed')"
          />
          <!-- WIDGET_FRAME within WIDGET_LAYOUT_COLUMNS -->
          <overlay-data-widget-frame
            v-else-if="
              isWidget(colFieldOrWidget.name) &&
                colFieldOrWidget.kind === 'WIDGET_FRAME'
            "
            ref="wrappedWidgets"
            :key="
              'renderable-' +
                renderableIndex +
                '-layout-column-widget-frame-' +
                colFieldOrWidgetIndex
            "
            :name="colFieldOrWidget.name"
            :thickness="colFieldOrWidget.field_mapping.thickness"
            :color="colFieldOrWidget.field_mapping.color"
            :color-palette="colorPalette"
            :warnings="getExtraWarningsForWidget(colFieldOrWidget)"
            class="mb-3"
            @changed="$emit('changed')"
          />
        </template>
      </v-layout>
    </v-layout>
  </v-layout>
</template>

<script>
import overlayDataExtraWarnings from '@/mixins/overlayDataExtraWarnings.js';

import OverlayDataField from '@/components/OverlayDataField';
import OverlayDataWidgetButton from '@/components/OverlayDataWidgetButton';
import OverlayDataWidgetFrame from '@/components/OverlayDataWidgetFrame';
import OverlayDataWidgetImage from '@/components/OverlayDataWidgetImage';
import OverlayDataWidgetPrice from '@/components/OverlayDataWidgetPrice';
import OverlayDataWidgetText from '@/components/OverlayDataWidgetText';

export default {
  components: {
    OverlayDataField,
    OverlayDataWidgetButton,
    OverlayDataWidgetFrame,
    OverlayDataWidgetImage,
    OverlayDataWidgetPrice,
    OverlayDataWidgetText,
  },
  mixins: [overlayDataExtraWarnings],
  props: {
    widget: {
      type: Object,
      required: true,
    },
    renderableIndex: {
      type: Number,
      required: true,
    },
    colorPalette: {
      type: Array,
      required: true,
    },
    // See parent component for actual shape.
    extraWarnings: {
      type: Array[Object],
      required: true,
    },
    isField: {
      type: Function,
      required: true,
    },
    isWidget: {
      type: Function,
      required: true,
    },
  },
  methods: {
    validate() {
      let allValid = true;

      if (this.$refs.actualWidget) {
        if (!this.$refs.actualWidget.validate()) {
          allValid = false;
        }
      }

      if (this.$refs.wrappedFields) {
        this.$refs.wrappedFields.forEach((fieldRef) => {
          if (!fieldRef.validate()) {
            allValid = false;
          }
        });
      }

      if (this.$refs.wrappedWidgets) {
        this.$refs.wrappedWidgets.forEach((widgetRef) => {
          if (!widgetRef.validate()) {
            allValid = false;
          }
        });
      }

      return allValid;
    },
    getOverlayData() {
      let overlayData = {
        _labels: {},
      };

      if (this.$refs.actualWidget) {
        const widgetOverlayData = this.$refs.actualWidget.getOverlayData();
        overlayData = {...overlayData, ...widgetOverlayData};
      }

      if (this.$refs.wrappedFields) {
        this.$refs.wrappedFields.forEach((fieldRef) => {
          const fieldData = fieldRef.getData();
          overlayData[fieldData.name] = fieldData.value;
          overlayData._labels[fieldRef.name] = fieldData.label;
        });
      }

      if (this.$refs.wrappedWidgets) {
        this.$refs.wrappedWidgets.forEach((widgetRef) => {
          const widgetOverlayData = widgetRef.getOverlayData();
          overlayData = {...overlayData, ...widgetOverlayData};
        });
      }

      return overlayData;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/OverlayDataWidget.vue